import * as path from "path";

import { pathExists, readFile, remove, writeFile, writeJson } from "fs-extra";

import {
    getLastStableEngineVersion,
    getLastStableGeneratorVersion,
    getPrereleaseVersionFormJson,
    isEngineVersionExists,
} from "../../BuildTasks/PublishedVersionUtils";
import { isNewFormJsonFormat, readFormConfig } from "../../BuildTasks/ResolveFormInfo";
import { ConsoleApplicationContext } from "../../Commons/ConsoleApplication/ConsoleApplicationContext";
import { FormGlobs } from "../../Commons/FormGlobsUtils";
import { ILogger } from "../../Commons/Logging/ILogger";
import { parseCandyVersionSpec } from "../../BuildTasks/Version";
import { WellKnownDirectories } from "../../../../Common/WellKnownDirectories";

import { UpgradeFormOptions } from "./UpgradeFormOptions";

export async function runUpgradeForm(
    context: ConsoleApplicationContext,
    options: UpgradeFormOptions,
    farmDirectory: string
): Promise<void> {
    const formGlobs = await FormGlobs.resolveSpecifiers(options.formGlobs, farmDirectory);
    for (const { gfv, formPath } of await FormGlobs.resolveGfvGlobs(formGlobs, farmDirectory)) {
        const logger = context.logger.withPrefix(`FORM:${gfv}`);
        if (options.useCiBuild) {
            logger.log("Begin upgrading build mode");
            await upgradeFormToCiBuildMode(formPath, logger);
            logger.log("Finish upgrading build mode");
        }
        if (options.engine != undefined) {
            logger.log("Begin upgrading engine version");
            await upgradeFormEngine(formPath, options.engine, options.skipPreVersions, logger);
            logger.log("Finish upgrading engine version");
        }
        if (options.preid != undefined) {
            logger.log("Begin upgrading engine version");
            await upgradeFormPrerelease(formPath, options.preid, logger);
            logger.log("Finish upgrading engine version");
        }
    }
}

async function upgradeFormEngine(
    formPath: string,
    engineVersion: string,
    skipPreVersions: boolean,
    logger: ILogger
): Promise<void> {
    const formJsonPath = path.join(formPath, WellKnownDirectories.FormJsonFileName);
    if (!(await pathExists(formJsonPath))) {
        logger.warning("Form does not have form.json: Upgrade skipped");
        return;
    }
    const formConfig = await readFormConfig(formJsonPath);
    if (skipPreVersions) {
        if (formConfig.dependencies?.engine != undefined && formConfig.dependencies?.generator != undefined) {
            const engineVersionSpec = parseCandyVersionSpec(formConfig.dependencies?.engine);
            const generatorVersionSpec = parseCandyVersionSpec(formConfig.dependencies?.generator);
            if (
                engineVersionSpec.type === "pre" ||
                generatorVersionSpec.type === "pre" ||
                (engineVersionSpec.type === "version" && /[a-zA-Z]/.test(engineVersionSpec.version)) ||
                (generatorVersionSpec.type === "version" && /[a-zA-Z]/.test(generatorVersionSpec.version))
            ) {
                logger.log(
                    "Form version has prerelease version: Upgrade skipped due to flag skipPreVersions (--skip-pre-versions) specified"
                );
                return;
            }
        }
    }
    if (formConfig.mode === "no-editor") {
        logger.log("Form does not have editor: Upgrade skipped");
        return;
    }
    if (engineVersion === "latest") {
        formConfig.dependencies = {
            engine: await getLastStableEngineVersion(),
            generator: await getLastStableGeneratorVersion(),
        };
        logger.log(`Updated to last stable version ${formConfig.dependencies.engine}`);
        await writeJson(formJsonPath, formConfig, { spaces: 2 });
    } else {
        if (await isEngineVersionExists(engineVersion)) {
            formConfig.dependencies = { engine: engineVersion, generator: engineVersion };
            logger.log(`Updated to version ${formConfig.dependencies.engine}`);
            await writeJson(formJsonPath, formConfig, { spaces: 2 });
        } else {
            throw new Error(`Версии ${engineVersion} не существует.`);
        }
    }
}

async function upgradeFormToCiBuildMode(formPath: string, logger: ILogger): Promise<void> {
    const formJsonPath = path.join(formPath, WellKnownDirectories.FormJsonFileName);
    if (!(await pathExists(formJsonPath))) {
        logger.warning("form.json does not exists. Upgrade skipped.");
        return;
    }
    const generatedFormDirectory = path.join(formPath, "form");
    if (!(await isNewFormJsonFormat(formJsonPath))) {
        throw new Error(
            "Файл form.json формы должен иметь новый формат зависимости. Обратитесь к разработчикам за разъяснениями."
        );
    }
    const formConfig = await readFormConfig(formJsonPath);
    if (formConfig.build !== "ci") {
        formConfig.build = "ci";
        await writeJson(formJsonPath, formConfig, { spaces: 2 });
        logger.log("Updated form.json");
    }
    if (await pathExists(generatedFormDirectory)) {
        await remove(generatedFormDirectory);
        logger.log("Removed form directory");
    }
    const formGitIgnorePath = path.join(formPath, ".gitignore");
    if (await pathExists(formGitIgnorePath)) {
        const gitIgnoreContent = await readFile(formGitIgnorePath, "utf8");
        if (!gitIgnoreContent.split("\n").includes("form")) {
            await writeFile(formGitIgnorePath, gitIgnoreContent.trimRight() + "\n" + "form\n");
            logger.log("Updated .gitignore");
        }
    } else {
        await writeFile(formGitIgnorePath, "form\n");
        logger.log("Created .gitignore");
    }
}

async function upgradeFormPrerelease(formPath: string, preid: string, logger: ILogger): Promise<void> {
    const formJsonPath = path.join(formPath, WellKnownDirectories.FormJsonFileName);
    const formConfig = await readFormConfig(formJsonPath);
    const prereleaseVersionFormJson = await getPrereleaseVersionFormJson(preid);

    if (prereleaseVersionFormJson != undefined) {
        formConfig.dependencies = { engine: prereleaseVersionFormJson, generator: prereleaseVersionFormJson };
        logger.log(`Updated to prerelease version ${formConfig.dependencies.engine}`);
        await writeJson(formJsonPath, formConfig, { spaces: 2 });
    } else {
        throw new Error(`Версии ${preid} не существует.`);
    }
}
